<template>
  <!-- 2026-09-03：仅平台超级管理员显示维护总开关，文案与状态统一保存到平台扩展设置。 -->
  <!-- 2026-09-17：插槽让入口归入齿轮，组件自身也按当前身份和有效权限限制打开/保存。 -->
  <div v-if="allowed" class="maintenance-control">
    <slot :open-panel="openPanel" :enabled="config.enabled">
    <a-tooltip title="系统维护">
      <a-button class="maintenance-trigger" type="text" @click="openPanel">
        <template #icon><ToolOutlined /></template>
        <span>系统维护</span>
        <i v-if="config.enabled"></i>
      </a-button>
    </a-tooltip>
    </slot>

    <a-modal
      v-model:visible="visible"
      title="系统维护"
      :footer="null"
      :width="620"
      :mask-closable="false"
    >
      <a-spin :spinning="loading || saving">
        <a-alert
          :type="config.enabled ? 'warning' : 'info'"
          :message="config.enabled ? '维护模式已开启' : '当前正常运行'"
          :description="config.enabled
            ? '除本标签页和应急入口外，平台、分店、网页端、管理端及小程序将在检查状态后显示维护页。'
            : '开启后仅限制各端界面访问，不会停止或拦截后端接口。'"
          show-icon
        />

        <a-form class="maintenance-form" layout="vertical">
          <a-form-item label="维护页标题" required>
            <a-input v-model:value="draft.title" :maxlength="40" show-count />
          </a-form-item>
          <a-form-item label="维护说明" required>
            <a-textarea
              v-model:value="draft.message"
              :maxlength="500"
              :rows="6"
              show-count
              placeholder="请输入维护期间向用户显示的说明"
            />
          </a-form-item>
          <a-form-item label="应急管理入口">
            <a-input :value="recoveryUrl" readonly>
              <template #addonAfter>
                <span class="copy-link" @click="copyRecoveryUrl">复制</span>
              </template>
            </a-input>
            <div class="form-tip">维护期间访问此地址，使用平台超级管理员账号验证后可关闭维护。</div>
          </a-form-item>
        </a-form>

        <div class="maintenance-actions">
          <a-button @click="visible=false">关闭窗口</a-button>
          <a-button v-if="config.enabled" @click="saveMessage">保存文案</a-button>
          <a-popconfirm
            v-if="config.enabled"
            title="确认关闭维护并恢复所有终端访问吗？"
            ok-text="确认关闭"
            cancel-text="取消"
            @confirm="changeMaintenance(false)"
          >
            <a-button type="primary">关闭维护</a-button>
          </a-popconfirm>
          <a-popconfirm
            v-else
            title="确认开启系统维护吗？"
            ok-text="确认开启"
            cancel-text="取消"
            @confirm="changeMaintenance(true)"
          >
            <a-button type="primary" danger>开启系统维护</a-button>
          </a-popconfirm>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { ToolOutlined } from '@ant-design/icons-vue'
import store from '@/store'
import { canManageSystemMaintenance } from '@/utils/platformAdministration'
import sysRequest from '@/api/sys'
import {
  clearMaintenanceBypass,
  DEFAULT_MAINTENANCE_CONFIG,
  loadPlatformMaintenance,
  MAINTENANCE_ACCESS_PATH,
  notifyMaintenanceChanged,
  savePlatformMaintenance,
  setMaintenanceBypass,
  SystemMaintenanceConfig
} from '@/utils/maintenance'

export default defineComponent({
  name: 'SystemMaintenanceControl',
  components: { ToolOutlined },
  setup() {
    const allowed=computed(()=>canManageSystemMaintenance(store.getters.userInfo,store.getters.functions?.allFunc))
    const visible = ref(false)
    const loading = ref(false)
    const saving = ref(false)
    const config = ref<SystemMaintenanceConfig>({ ...DEFAULT_MAINTENANCE_CONFIG })
    const draft = reactive({
      title: DEFAULT_MAINTENANCE_CONFIG.title,
      message: DEFAULT_MAINTENANCE_CONFIG.message
    })
    const recoveryUrl = computed(() => `${window.location.origin}${MAINTENANCE_ACCESS_PATH}`)

    const applySnapshot = (next: SystemMaintenanceConfig) => {
      config.value = { ...next }
      draft.title = next.title
      draft.message = next.message
    }

    const openPanel = async () => {
      if(!allowed.value){message.warning('仅平台超级管理员可管理系统维护。');return}
      visible.value = true
      loading.value = true
      try {
        const snapshot = await loadPlatformMaintenance()
        applySnapshot(snapshot.config)
      } catch (_error) {
        message.error('维护状态读取失败，请稍后重试。')
      } finally {
        loading.value = false
      }
    }

    const validateDraft = () => {
      draft.title = draft.title.trim()
      draft.message = draft.message.trim()
      if (!draft.title || !draft.message) {
        message.warning('维护页标题和维护说明不能为空。')
        return false
      }
      return true
    }

    const persist = async (enabled: boolean, successText: string, logEventName: string) => {
      if(!allowed.value){message.warning('仅平台超级管理员可管理系统维护。');return}
      if (!validateDraft()) return
      saving.value = true
      try {
        const user = store.getters.userInfo || {}
        const updatedBy = user.loginName || user.nickName || `operator-${user.id || 'unknown'}`
        const saved = await savePlatformMaintenance({
          enabled,
          title: draft.title,
          message: draft.message
        }, updatedBy)
        if (enabled) setMaintenanceBypass(saved)
        else clearMaintenanceBypass()
        applySnapshot(saved)
        notifyMaintenanceChanged(saved)
        sysRequest.operationLog({ eventName: logEventName })
          .catch(() => undefined)
        message.success(successText)
      } catch (error: any) {
        message.error(error?.message || '保存失败，请稍后重试。')
      } finally {
        saving.value = false
      }
    }

    const changeMaintenance = (enabled: boolean) =>
      persist(
        enabled,
        enabled ? '系统维护已开启。' : '系统维护已关闭，所有终端将自动恢复。',
        enabled ? '开启系统维护' : '关闭系统维护'
      )

    const saveMessage = () => persist(true, '维护文案已更新。', '更新系统维护文案')

    const copyRecoveryUrl = async () => {
      try {
        await navigator.clipboard.writeText(recoveryUrl.value)
        message.success('应急管理入口已复制。')
      } catch (_error) {
        message.warning(`请手动复制：${recoveryUrl.value}`)
      }
    }

    return {
      allowed,
      visible,
      loading,
      saving,
      config,
      draft,
      recoveryUrl,
      openPanel,
      changeMaintenance,
      saveMessage,
      copyRecoveryUrl
    }
  }
})
</script>

<style lang="scss" scoped>
.maintenance-control {
  display: flex;
  align-items: center;
}
.maintenance-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 34px;
  padding: 0 9px;
  color: #475467;
  font-size: 13px;
  i {
    position: absolute;
    top: 3px;
    right: 2px;
    width: 7px;
    height: 7px;
    border: 1px solid #ffffff;
    border-radius: 50%;
    background: #ff4d4f;
  }
}
.maintenance-form {
  margin-top: 22px;
}
.form-tip {
  margin-top: 7px;
  color: #8c8c8c;
  font-size: 12px;
}
.copy-link {
  color: #096dd9;
  cursor: pointer;
}
.maintenance-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}
</style>
